/**
 * Conservatively strips Markdown *formatting syntax* from an AI text response
 * while preserving the actual content. This runs on every plain-text AI
 * response returned by getByQueryChat, so it must never destroy legitimate
 * content: hashtags (#trending), prices ($9.99), raw URLs, emojis, or
 * intentional line breaks.
 *
 * Deliberate guards:
 *  - Heading rule requires a space after the hashes (`# Title`), so `#hashtag`
 *    survives untouched.
 *  - Link rule collapses to the raw URL (the app inserts URLs without brackets).
 *  - Whitespace/newlines are NOT collapsed — brainstorm lists and multi-line
 *    posts keep their structure.
 */
export const stripMarkdown = (text) => {
    if (!text || typeof text !== "string") return text || "";

    let t = text;

    // Fenced code blocks → keep the inner content, drop the ``` fences.
    t = t.replace(/```[a-zA-Z0-9]*\n?([\s\S]*?)```/g, "$1");
    // Inline code → keep content, drop the backticks.
    t = t.replace(/`([^`]+)`/g, "$1");

    // Bold / italic (asterisks) → keep the inner text.
    t = t.replace(/\*\*(.+?)\*\*/g, "$1");
    t = t.replace(/\*(.+?)\*/g, "$1");
    // Bold / italic (underscores) → only at word boundaries, so @john_doe,
    // file_names, snake_case and 5_000 survive untouched.
    t = t.replace(/(^|[^\w])__(\S.*?\S|\S)__(?!\w)/g, "$1$2");
    t = t.replace(/(^|[^\w])_(\S.*?\S|\S)_(?!\w)/g, "$1$2");
    // Strikethrough → keep the inner text.
    t = t.replace(/~~(.*?)~~/g, "$1");

    // Markdown links: prefer the raw URL, else the link text.
    t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$2");
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");

    // Line-start heading hashes (space required so #hashtags survive).
    t = t.replace(/^#{1,6}\s+/gm, "");
    // Line-start blockquote markers.
    t = t.replace(/^>\s?/gm, "");
    // Line-start unordered list markers.
    t = t.replace(/^\s*[-*+]\s+/gm, "");
    // Line-start ordered list markers ("1. ").
    t = t.replace(/^\s*\d+\.\s+/gm, "");

    return t.trim();
};

export default stripMarkdown;
